import { useState, useEffect } from 'react'
import Header from '../components/Header'
import Footer from '../components/Footer'

export default function FAQ() {
  const [openIndex, setOpenIndex] = useState(null)

  useEffect(() => {
    document.title = 'Job Scam FAQ — Common Questions Answered | ScamView'
  }, [])

  const faqs = [
    {
      question: "How does ScamView detect scams?",
      answer: "ScamView uses advanced AI to analyze multiple signals including urgency tactics, pressure patterns, suspicious financial requests, domain authenticity, and cross-references with known scam databases. Our system combines technical verification (DNS, VOIP detection) with behavioral analysis to provide a comprehensive risk assessment."
    },
    {
      question: "How accurate is the scam detection?",
      answer: "ScamView combines over 10 detection signals — DNS authentication, domain age, SSL checks, web reputation, language analysis, and AI reasoning — to produce a risk score. No automated system is perfect. Results should be used as guidance alongside your own judgment."
    },
    {
      question: "What information should I scan?",
      answer: "You can scan recruitment emails, LinkedIn messages, text messages, phone numbers, or any communication from a potential employer. Include as much context as possible - sender details, message content, company name, and any links or attachments mentioned."
    },
    {
      question: "Is my data secure and private?",
      answer: "Yes. All data is encrypted in transit and at rest. We don't share your personal information with third parties. Only the target you scan (email or phone number) and the analysis result are stored — message content is processed in real-time and not permanently saved."
    },
    {
      question: "What's the difference between Free and Premium?",
      answer: "Free visitors get a single scan with no account required. Basic ($9.99/month) includes scan history and increased scan credits. Premium ($19.99/month) adds HireMail — a protected email alias that automatically scans recruiter emails before they reach your inbox."
    },
    {
      question: "Can ScamView guarantee I won't be scammed?",
      answer: "No service can guarantee 100% protection. Scammers constantly evolve their tactics. ScamView significantly improves your ability to identify scams, but you should always exercise caution, verify information independently, and trust your instincts."
    },
    {
      question: "What if a legitimate recruiter is flagged as suspicious?",
      answer: "False positives can occur if a legitimate recruiter uses aggressive language or unconventional methods. Check our detailed report for specific red flags. You can verify legitimacy by calling the company directly (using a number from their official website, not provided by the recruiter), checking LinkedIn profiles, and researching the company independently."
    },
    {
      question: "How quickly do I get scan results?",
      answer: "Most scans complete within 30–60 seconds. First-time scans of a target may take longer as we run DNS lookups, web searches, and AI analysis in parallel. Cached results for recently scanned targets return much faster."
    },
    {
      question: "Can I scan phone numbers?",
      answer: "Yes! Enter a phone number and we'll check if it's a VOIP number (commonly used by scammers), look up the carrier, and analyze it against known risk patterns."
    },
    {
      question: "What should I do if I find a scam?",
      answer: "If ScamView identifies a scam: (1) Don't respond or engage further, (2) Don't click any links or download attachments, (3) Report it to the platform where you received it (LinkedIn, email provider, etc.), (4) Use our Report feature to help protect others, (5) Consider reporting to the FTC at reportfraud.ftc.gov."
    },
    {
      question: "Does ScamView work for international recruitment?",
      answer: "Yes. Our system analyzes communications in multiple languages and verifies domain registrations worldwide. Web reputation checks and DNS lookups work across international domains."
    },
    {
      question: "Can I cancel my subscription anytime?",
      answer: "Yes, you can cancel anytime from your Settings page. You'll retain Premium access until the end of your current billing period. No refunds for partial months, but you can always re-subscribe later."
    }
  ]

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index)
  }

  return (
    <div className="min-h-screen bg-[#0a0e17]">
      <Header />

      <div className="container mx-auto px-4 py-12 max-w-4xl">
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4" style={{ fontFamily: "'Orbitron', sans-serif" }}>
            Frequently Asked Questions
          </h1>
          <p className="text-gray-400 text-lg">
            Everything you need to know about ScamView
          </p>
        </div>

        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div key={index} className="card border border-white/10">
              <button
                onClick={() => toggleFAQ(index)}
                className="w-full text-left flex justify-between items-start gap-4 p-6"
              >
                <h3 className="text-lg font-semibold text-white">
                  {faq.question}
                </h3>
                <svg
                  className={`w-5 h-5 text-cyan-400 flex-shrink-0 transform transition-transform ${
                    openIndex === index ? 'rotate-180' : ''
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {openIndex === index && (
                <div className="px-6 pb-6">
                  <p className="text-gray-300 leading-relaxed">
                    {faq.answer}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="card mt-12 text-center">
          <h2 className="text-2xl font-bold text-white mb-2">Still have questions?</h2>
          <p className="text-gray-400 mb-4">
            Can't find the answer you're looking for? Contact our support team.
          </p>
          <button className="btn-primary">
            Contact Support
          </button>
        </div>
      </div>

      <Footer />
    </div>
  )
}
